// 初始表单数据
const INITIAL_FORM_DATA: WorkspaceCreate = {
  name: '',
  category: 'male' as WorkspaceCategory,
  genre: '',
  total_chapters: 100,
  words_per_chapter: 3000,
  summary: '',
  tags: []
};

// 基础信息图标
const BasicInfoIcon: React.FC = () => (
  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// 配置图标
const ConfigIcon: React.FC = () => (
  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

/**
 * 创建工作区对话框组件
 *
 * 允许用户填写工作区名称、选择分类（男频/女频）、选择小说类型、
 * 设置章节数量、每章节字数。
 *
 * 架构说明:
 * - 使用HTTP REST API创建工作区（不依赖WebSocket）
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import type { WorkspaceCategory, WorkspaceCreate, Workspace } from '../../types/workspace';
import { workspaceApi, QuotaExceededError, ConflictError, ValidationError as HttpValidationError } from '../../services/workspaceApi';
import { TagSelector } from '../tag/TagSelector';

// 小说类型列表
const MALE_GENRES = [
  '玄幻', '奇幻', '武侠', '仙侠', '都市', '现实',
  '军事', '历史', '游戏', '体育', '科幻', '诸天无限',
  '悬疑', '轻小说', '短篇'
] as const;

const FEMALE_GENRES = [
  '古代言情', '仙侠奇缘', '现代言情', '浪漫青春',
  '玄幻言情', '悬疑推理', '短篇', '科幻空间',
  '游戏竞技', '轻小说', '现实生活'
] as const;

// 表单验证函数
const validateFormData = (data: WorkspaceCreate): Record<string, string> => {
  const errors: Record<string, string> = {};

  // 名称验证
  if (!data.name || data.name.trim().length < 1 || data.name.trim().length > 20) {
    errors.name = '工作区名称长度必须在 1-20 字符之间';
  }
  
  // 检查是否包含汉字
  if (data.name && !/[\u4e00-\u9fff]/.test(data.name.trim())) {
    errors.name = '工作区名称必须包含至少一个汉字';
  }

  // 类型验证
  if (!data.genre) {
    errors.genre = '请选择小说类型';
  }

  // 章节数量验证
  if (data.total_chapters < 1 || data.total_chapters > 10000) {
    errors.total_chapters = '章节数量必须在1-10000之间';
  }

  // 每章节字数验证
  if (data.words_per_chapter < 500 || data.words_per_chapter > 50000) {
    errors.words_per_chapter = '每章节字数必须在500-50000之间';
  }

  // 简介验证
  if (data.summary && data.summary.length > 500) {
    errors.summary = '小说简介不能超过500字符';
  }

  return errors;
};

// 表单字段输入组件
interface FormFieldProps {
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}

const FormField: React.FC<FormFieldProps> = ({ label, error, required, children }) => {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-900 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

// 表单分组标题组件
interface GroupTitleProps {
  title: string;
  icon: React.ReactNode;
}

const GroupTitle: React.FC<GroupTitleProps> = ({ title, icon }) => {
  return (
    <div className="flex items-center gap-2 mb-4 mt-6">
      {icon}
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      <div className="flex-1 h-px bg-gray-200"></div>
    </div>
  );
};

// 主组件 Props
interface CreateWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateSuccess?: (workspace: Workspace) => void;
}

export const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({
  isOpen,
  onClose,
  onCreateSuccess
}) => {
  // 表单状态
  const [formData, setFormData] = useState<WorkspaceCreate>(INITIAL_FORM_DATA);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // 获取当前分类对应的类型列表
  const genreOptions = useMemo(() => {
    return formData.category === 'male' ? MALE_GENRES : FEMALE_GENRES;
  }, [formData.category]);

  // 计算目标字数（万字）
  const targetWords = useMemo(() => {
    return ((formData.total_chapters * formData.words_per_chapter) / 10000).toFixed(1);
  }, [formData.total_chapters, formData.words_per_chapter]);

  // 当分类改变时，重置类型选择和标签
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      genre: ''
    }));
    setErrors(prev => ({
      ...prev,
      genre: ''
    }));
    // 清除已选择的标签
    setSelectedTagIds([]);
  }, [formData.category]);

  // 当流派改变时，清除已选择的标签（因为标签是基于流派匹配的）
  useEffect(() => {
    if (formData.genre) {
      setSelectedTagIds([]);
    }
  }, [formData.genre]);

  // 处理字段变化
  const handleFieldChange = useCallback(<K extends keyof WorkspaceCreate>(
    field: K,
    value: WorkspaceCreate[K]
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // 清除该字段的错误
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  // 处理表单提交（使用HTTP API）
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // 清除表单级错误
    setFormError(null);

    // 验证表单
    const validationErrors = validateFormData(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      // 使用 Cookie 认证创建工作区
      const newWorkspace = await workspaceApi.createWorkspace(undefined, formData);

      // 创建成功
      onCreateSuccess?.(newWorkspace);
      onClose();
      resetForm();
    } catch (error) {
      // 记录详细错误到日志
      console.error('[CreateWorkspaceModal] 创建工作区失败:', error);

      if (error instanceof QuotaExceededError) {
        setFormError('已达免费版工作区上限(2个)，请升级订阅版');
      } else if (error instanceof ConflictError) {
        setErrors({ name: '工作区名称已存在' });
      } else if (error instanceof HttpValidationError) {
        setFormError(error.message);
      } else if (error instanceof Error) {
        setFormError(error.message || '创建失败，请检查网络连接后重试');
      } else {
        setFormError('创建失败，请检查网络连接后重试');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onCreateSuccess, onClose]);

  // 重置表单
  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setSelectedTagIds([]);
    setErrors({});
    setFormError(null);
  }, []);

  // 处理遮罩点击
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // 图标组件
  const BasicInfoIconComponent = useMemo(() => <BasicInfoIcon />, []);
  const ConfigIconComponent = useMemo(() => <ConfigIcon />, []);

  // 不显示时返回null
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleOverlayClick}
    >
      <div className="bg-white border border-gray-200 rounded-2xl shadow-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* 对话框标题 */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">创建新工作区</h2>
            <button
              onClick={onClose}
              className="text-gray-700 hover:text-gray-900 transition-colors"
              aria-label="关闭"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* 表单内容 */}
        <form onSubmit={handleSubmit} className="p-8">
          {/* 表单级错误 */}
          {formError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-700">{formError}</p>
              </div>
            </div>
          )}

          {/* 基本信息 */}
          <GroupTitle title="基本信息" icon={BasicInfoIconComponent} />

          <FormField label="工作区名称" error={errors.name} required>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder="请输入工作区名称"
              maxLength={20}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white
                ${errors.name ? 'border-red-300' : 'border-gray-200'}`}
            />
          </FormField>

          <FormField label="分类" required>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="category"
                  value="male"
                  checked={formData.category === 'male'}
                  onChange={(e) => handleFieldChange('category', e.target.value as WorkspaceCategory)}
                  className="w-4 h-4 text-gray-600 focus:ring-gray-500"
                />
                <span className="text-gray-900">男频</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="category"
                  value="female"
                  checked={formData.category === 'female'}
                  onChange={(e) => handleFieldChange('category', e.target.value as WorkspaceCategory)}
                  className="w-4 h-4 text-gray-600 focus:ring-gray-500"
                />
                <span className="text-gray-900">女频</span>
              </label>
            </div>
          </FormField>

          <FormField label="小说类型" error={errors.genre} required>
            <select
              value={formData.genre}
              onChange={(e) => handleFieldChange('genre', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white
                ${errors.genre ? 'border-red-300' : 'border-gray-200'}`}
            >
              <option value="">请选择小说类型</option>
              {genreOptions.map(genre => (
                <option key={genre} value={genre}>{genre}</option>
              ))}
            </select>
          </FormField>

          <FormField label="小说简介">
            <textarea
              value={formData.summary}
              onChange={(e) => handleFieldChange('summary', e.target.value)}
              placeholder="优秀的小说简介有助于大模型进行更好的创作（可选）"
              maxLength={500}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white resize-none
                ${errors.summary ? 'border-red-300' : 'border-gray-200'}`}
            />
            <div className="mt-1 text-xs text-gray-700">
              {formData.summary?.length || 0} / 500
            </div>
          </FormField>

          {/* 标签选择 */}
          {formData.genre && (
            <div className="mb-4">
              <TagSelector
                category={formData.category}
                genre={formData.genre}
                selectedTagIds={selectedTagIds}
                onChange={(tagIds) => {
                  setSelectedTagIds(tagIds);
                  handleFieldChange('tags', tagIds);
                }}
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* 创作配置 */}
          <GroupTitle title="创作配置" icon={ConfigIconComponent} />

          <div className="grid grid-cols-2 gap-4">
            <FormField label="目标章节数" error={errors.total_chapters} required>
              <input
                type="number"
                value={formData.total_chapters}
                onChange={(e) => handleFieldChange('total_chapters', parseInt(e.target.value) || 0)}
                min={1}
                max={10000}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white
                  ${errors.total_chapters ? 'border-red-300' : 'border-gray-200'}`}
              />
            </FormField>

            <FormField label="每章节字数" error={errors.words_per_chapter} required>
              <input
                type="number"
                value={formData.words_per_chapter}
                onChange={(e) => handleFieldChange('words_per_chapter', parseInt(e.target.value) || 0)}
                min={500}
                max={50000}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white
                  ${errors.words_per_chapter ? 'border-red-300' : 'border-gray-200'}`}
              />
            </FormField>
          </div>

          {/* 目标字数计算结果 */}
          <div className="mb-6 p-3 bg-gray-100 border border-gray-200 rounded-md">
            <div className="flex items-center justify-between">
              <span className="text-gray-900 font-medium">目标总字数：</span>
              <span className="text-gray-900 font-bold text-lg">{targetWords} 万字</span>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex justify-end gap-3 mt-8">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-md font-medium transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-ink-800 hover:bg-ink-700 text-white rounded-md font-medium shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  创建中...
                </>
              ) : (
                '创建工作区'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateWorkspaceModal;
